export function isDesktop() {
    return window.innerWidth >= LBRL_HEADERS.mbhThreshold;
}

export const EMAIL_RE = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 *
 * @param {string}      name
 * @param {EventInit}   args
 * @returns {Event}
 */
export function createEvent(name, args = {}) {
    let event;
    if (typeof Event === 'function') {
        event = new Event(name, Object.assign({}, {
            bubbles: true,
            cancelable: true,
        }, args));
    } else {
        event = document.createEvent('Event');
        event.initEvent(name, true, true);
    }
    return event;
}


/**
 *
 * @param {HTMLElement|Node} el
 * @param {string|keyof HTMLElementTagNameMap} selector
 * @returns {HTMLElement|null}
 */
export function locateEnclosingElement(el, selector) {
    let currEl = el;
    while (currEl !== window.document.body) {
        if (currEl.matches(selector)) {
            return currEl;
        }
        currEl = currEl.parentElement;
    }
    return null;
}


export function formatDateYmd(date) {
    let d     = new Date(date),
        month = '' + (d.getMonth() + 1),
        day   = '' + d.getDate(),
        year  = d.getFullYear();

    if (month.length < 2) {
        month = '0' + month;
    }
    if (day.length < 2) {
        day = '0' + day;
    }

    return [year, month, day].join('-');
}

/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 * @param func
 * @param wait
 * @param immediate
 * @returns {function(): void}
 */
export function debounce(func, wait, immediate) {
    var timeout;
    return function() {
        var context = this, args = arguments;
        var later = function() {
            timeout = null;
            if (!immediate) {
                func.apply(context, args);
            }
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) {
            func.apply(context, args);
        }
    };
}


export function initMissingImageSubstitution(replacementUrl) {

    const extractUrlsRe = /["'\s\b]?((?:https?:\/)?\/[^"'\s\b]+)/ig;
    const processedUrls = {};
    let processingUrls = [];

    traverse(document.documentElement);

    const observer = new MutationObserver(async list => {
        for (let mutation of list) {
            if (mutation.type === 'childList') {
                mutation.target instanceof HTMLElement && await traverse(mutation.target);
            }
        }
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });

    /**
     *
     * @param {HTMLElement} element
     */
    async function traverse(element) {
        for (let child of element.children) {
            child instanceof HTMLElement && await traverse(child);
        }
        await apply(element);
    }

    /**
     *
     * @param {HTMLElement} element
     */
    async function apply(element) {
        if (element.tagName === 'IMG') {
            const [src, srcset] = [element.getAttribute('src'), element.getAttribute('srcset')];
            if (src) {
                element.setAttribute('src', await replaceUrl(src));
            }
            if (srcset) {
                element.setAttribute('srcset', await replaceMultiple(srcset));
            }
        } else if (element.style.backgroundImage) {
            element.style.backgroundImage = await replaceMultiple(element.style.backgroundImage);
        }
    }

    /**
     *
     * @param {string} urls
     * @returns {string}
     */
    async function replaceMultiple(urls) {
        const matches = [...urls.matchAll(extractUrlsRe)];
        let newUrls = urls;
        if (matches.length > 0) {
            for (let match of matches) {
                newUrls = newUrls.replace(match[1], await replaceUrl(match[1]));
            }
        }
        return newUrls;
    }

    /**
     *
     * @param {string} url
     * @returns {string}
     */
    async function replaceUrl(url) {
        if (processedUrls[url]) {
            return processedUrls[url];
        } else if (processingUrls.includes(url)) {
            return new Promise((resolve, reject) => {
                let counter = 0;
                let intval = setInterval(() => {
                    counter++;
                    if (processedUrls[url]) {
                        window.clearInterval(intval);
                        resolve(processedUrls[url]);
                    } else if (counter >= 100) {
                        reject();
                    }
                }, 500);
            });
        }

        processingUrls.push(url);

        const isLocalShortUrl = url.match(/^\/[^\/]+/);
        const isLocalLongUrl = url.indexOf(location.origin) >= 0;

        if (isLocalShortUrl || isLocalLongUrl) {
            const available = await isImageAvailable(url);

            if (available) {
                processedUrls[url] = url;
            } else if (isLocalShortUrl) {
                processedUrls[url] = `${replacementUrl}${url}`;
            } else if (isLocalLongUrl) {
                processedUrls[url] = url.replace(location.origin, replacementUrl);
            }
        } else {
            processedUrls[url] = url;
        }

        if (processedUrls[url] !== url) {
            console.log(`Replacing ${url} -> ${processedUrls[url]}`);
        }

        processingUrls = processingUrls.filter(u => u !== url);

        return processedUrls[url];
    }

    async function isImageAvailable(url) {
        const response = await window.fetch(url);
        return response.status >= 200 && response.status < 400;
    }
}


let youtubeApiCbs = [];
let loadingYoutubeApi = false;

let vimeoApiCbs = [];
let loadingVimeoApi = false;

let activeVideos = [];

export function withYoutubeApi(cb) {
    if (window.YT) {
        cb(window.YT);
    } else {
        youtubeApiCbs.push(cb);

        if (!loadingYoutubeApi) {
            loadingYoutubeApi = true;

            let prevFn = null;
            if (window.onYouTubeIframeAPIReady) {
                prevFn = window.onYouTubeIframeAPIReady;
            }
            window.onYouTubeIframeAPIReady = function() {
                if (typeof prevFn === 'function') {
                    prevFn();
                }
                youtubeApiCbs = youtubeApiCbs.filter(prevCb => {
                    prevCb(window.YT);
                    return false;
                });
                window.onYouTubeIframeAPIReady = null;
            };
            let scriptEl = document.createElement('script');
            scriptEl.src = 'https://www.youtube.com/iframe_api';
            scriptEl.async = true;
            document.head.appendChild(scriptEl);
        }
    }
}

export function withVimeoApi(cb) {
    if (window.Vimeo) {
        cb(window.Vimeo);
    } else {
        vimeoApiCbs.push(cb);
        if (!loadingVimeoApi) {
            loadingVimeoApi = true;

            let scriptEl = document.createElement('script');
            scriptEl.src = 'https://player.vimeo.com/api/player.js';
            scriptEl.async = true;
            scriptEl.addEventListener('load', () => {
                vimeoApiCbs = vimeoApiCbs.filter(prevCb => {
                    prevCb(window.Vimeo);
                    return false;
                });
            });
            document.head.appendChild(scriptEl);
        }
    }
}


export async function playVideo(htmlEl, options = {}) {
    return new Promise((resolve, reject) => {
        const { videoId, provider, background, fillSpace, modal } = Object.assign({}, {
            videoId: '',
            provider: '',
            background: false,
            fillSpace: true,
            /** @property {boolean} modal Plays video in modal. Current modal provider: featherlight */
            modal: false,
        }, options);

        if (modal && typeof jQuery.fn.featherlight === 'function') {
            initPlayBtn(playModalVideo);
            resolve({
                play: playModalVideo,
            });
        } else if (background) {
            playInlineVideo();
        } else {
            initPlayBtn(playInlineVideo);
        }

        function initPlayBtn(callback) {
            const playBtnEl = Object.assign(document.createElement('button'), {
                className: 'video-playBtn',
            });
            playBtnEl.appendChild(Object.assign(document.createElement('span'), {
                className: 'icon icon-play-circle',
            }));
            playBtnEl.addEventListener('click', callback);
            htmlEl.appendChild(playBtnEl);
        }

        function playModalVideo() {
            const content = (() => {
                switch (provider) {
                    case 'youtube':
                        return `<iframe src="https://www.youtube-nocookie.com/embed/${videoId}?feature=oembed&autoplay=1&fs=1&enablejsapi=1&rel=0" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
                    case 'vimeo':
                        return `<iframe src="https://player.vimeo.com/video/${videoId}?autoplay=1&autopause=1" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`
                    default:
                        reject(`Video provider '${provider}' not supported`);
                }
            })();
            jQuery.featherlight(`<div class="videoModal-inner">${content}</div>`, {
                variant: 'videoModal'
            });
        }

        function playInlineVideo() {

            let html = null;
            switch (provider) {
                case 'youtube':
                    html = `<iframe src="https://www.youtube-nocookie.com/embed/${videoId}?${background ? `modestbranding=1&mute=1&showinfo=0&loop=1&autohide=1&playsinline=1&playlist=${videoId}&` : ''}feature=oembed&autoplay=1&controls=${background ? '0' : 1}&fs=1&enablejsapi=1&rel=0" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>`
                    break;
                case 'vimeo':
                    html = `<iframe src="https://player.vimeo.com/video/${videoId}?${background ? 'loop=1&autopause=0&background=1&muted=1&' : ''}autoplay=1&autopause=1" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`
                    break;
                default:
                    reject(`Video provider '${provider}' not supported`);
            }

            htmlEl.innerHTML = html;

            const iframe = htmlEl.querySelector('iframe');
            let control;

            switch (provider) {
                case 'youtube':
                    withYoutubeApi(yt => {
                        const player = new yt.Player(iframe, {
                            events: {
                                onReady: () => {
                                    player.playVideo();
                                    htmlEl.classList.add('player--ready');
                                    resolve(control);
                                    const size = player.getSize();
                                    iframe.setAttribute('width', size.width);
                                    iframe.setAttribute('height', size.height);
                                    background && adjustBackgroundVideoAR();
                                },
                            },
                        });
                        control = {
                            get player() {
                                return player;
                            },
                            pause() {
                                return player.pauseVideo();
                            },
                            play() {
                                return player.playVideo();
                            },
                            async isPlaying() {
                                return player.getPlayerState() === 1 || player.getPlayerState() === 3;
                            },
                            onPlayStateChange(cb) {
                                player.addEventListener('stateChange', event => {
                                    switch (event.data) {
                                        case 0:
                                            cb('ended');
                                            break;
                                        case 1:
                                            cb('playing');
                                            break;
                                        case 2:
                                            cb('paused');
                                            break;
                                        case 3:
                                            cb('buffering');
                                            break;
                                    }
                                });
                            },
                        };
                        activeVideos.push(control);
                    });
                    break;
                case 'vimeo':
                    withVimeoApi(vimeo => {
                        let player = new vimeo.Player(iframe);
                        player.on('loaded', () => {
                            htmlEl.classList.add('player--ready');
                            resolve(control);
                            player.getVideoWidth().then(width => player.element.setAttribute('width', width));
                            player.getVideoHeight().then(height => player.element.setAttribute('height', height));
                            background && adjustBackgroundVideoAR();
                        });
                        control = {
                            get player() {
                                return player;
                            },
                            pause() {
                                return player.pause();
                            },
                            play() {
                                return player.play();
                            },
                            async isPlaying() {
                                return !await player.getPaused();
                            },
                            onPlayStateChange(cb) {
                                player.on('play', () => cb('playing'));
                                player.on('pause', () => cb('paused'));
                                player.on('ended', () => cb('ended'));
                                player.on('bufferstart', () => cb('buffering'));
                            },
                        };
                        activeVideos.push(control);
                    });
            }

            if (background) {

                setInterval(adjustBackgroundVideoAR, 500);
                window.addEventListener('resize', adjustBackgroundVideoAR);

                const observer = new IntersectionObserver(entries => entries.forEach(entry => {
                    if (control) {
                        if (entry.isIntersecting) {
                            control.play();
                        } else {
                            control.pause();
                        }
                    }
                }));

                observer.observe(htmlEl);
            }

            function adjustBackgroundVideoAR() {
                const blockRatio = htmlEl.offsetWidth / htmlEl.offsetHeight;
                const videoRatio = (parseInt(iframe.getAttribute('width')) || 16) / (parseInt(iframe.getAttribute('height')) || 9);
                if (fillSpace) {
                    const verticalFactor = blockRatio > 1 ? 1 : 1 / (blockRatio || 0.001);
                    const factor = verticalFactor + Math.abs(blockRatio - videoRatio);
                    iframe.style.transform = `scale(${factor})`;
                } else {
                    iframe.style.width = `${iframe.offsetHeight * videoRatio}px`;
                    iframe.style.left = `${(htmlEl.offsetWidth - iframe.offsetWidth) / 2}px`;
                }
            }
        }
    });
}

const observerCbs = [];
let mainMtObserver = null;

/**
 *
 * @param {string|{root: HTMLElement, selector: string}} selector  CSS selector for statically rendered elements
 * @param {(el: HTMLElement, idx: number, els: HTMLElement[]) => any} initEachFn  Callback for initialization. Is given
 *     the same arguments as Array.prototype.map and Array.prototype.forEach functions
 * @param args Advanced configuration
 * @param {boolean} args.observe Either to observe dynamically added elements
 * @param {(el: HTMLElement) => void} args.observerFilterFn Function that tests inserted elements for initialization
 *
 * @returns {unknown[]} Array of whatever your initEachFn function returns
 */
export function initElements(selector, initEachFn, args = {}) {
    args = Object.assign({}, {
        observe: false,
        observerFilterFn: null,
    }, args);

    const root = typeof selector === 'object' ? selector.root : document;
    const cssSelector = typeof selector === 'object' ? selector.selector : selector;

    if (mainMtObserver && args.observe && typeof args.observerFilterFn === 'function') {
        maybeInitMainMutationObserver();
        observerCbs.push((mutation => {
            [].slice.apply(mutation.nodeList).filter(args.observerFilterFn).forEach(initEachFn);
        }));
    }

    return [].slice.apply(root.querySelectorAll(cssSelector)).map(initEachFn);
}

function maybeInitMainMutationObserver() {
    if (!mainMtObserver && 'MutationObserver' in window) {
        mainMtObserver = new MutationObserver(mutations => mutations.forEach(mutation => {
            observerCbs.forEach(cb => cb(mutation));
        }));
        mainMtObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
}
